import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useDebounce } from "@uidotdev/usehooks";
import { DocumentArrowDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import useMessages from "../../data/useMessages";
import LeadsTable from "../../components/leads/LeadTable/LeadsTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import ExportLeads from "../../components/leads/LeadTable/ImportExport/ExportLeads";
import ImportLeads from "../../components/leads/LeadTable/ImportExport/ImportLeads";
import { ILead, LeadDocumentFilter, LeadSource, LeadStatus } from "../../models/Lead";
import LeadStats from "../../components/leads/LeadTable/LeadStats";

export default function Leads() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { unreadMessages } = useMessages();
  const [searchParams] = useSearchParams();
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [showDropOff, setShowDropOff] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const [showUnreadQueryString, setShowUnreadQueryString] = useState<boolean>(
    searchParams.get("unread") === "1" ? true : false
  );
  const [showRescheduleQueryString, setRescheduleQueryString] =
    useState<boolean>(searchParams.get("reschedule") === "1" ? true : false);
  const [lastSccImport, setLastSccImport] = useState<ILead | undefined>(
    undefined
  );
  const [currentStatusFilter, setCurrentStatusFilter] = useState("Awaiting Contact");
  const [currentStatusFilterIdx, setCurrentStatusFilterIdx] = useState(0);
  const [currentWebsiteFilter, setCurrentWebsiteFilter] = useState("All");
  const [currentWebsiteFilterIdx, setCurrentWebsiteFilterIdx] = useState(5);
  const [currentDocumentFilter, setCurrentDocumentFilter] = useState("All");
  const [currentDocumentFilterIdx, setCurrentDocumentFilterIdx] = useState(6);

  const statusFilters = [
    ...Object.values(LeadStatus).filter((ct) => isNaN(Number(ct))),
  ];

  const websiteFilters = [
    ...Object.values(LeadSource).filter((ct) => isNaN(Number(ct)))
  ];

  const documentFilters = [
    ...Object.values(LeadDocumentFilter).filter((ct) => isNaN(Number(ct))),
  ]

  useEffect(() => {
    setInfo({
      name: "Leads",
      desc: "View and manage your leads here",
    });
  }, []);

  useEffect(() => {
    if (showUnreadQueryString) {
      setShowUnread(true);
      setCurrentStatusFilter("All");
      setCurrentStatusFilterIdx(9);
      setShowUnreadQueryString(false);
    } else if (showRescheduleQueryString) {
      setCurrentStatusFilter("Reschedule");
      setCurrentStatusFilterIdx(LeadStatus.Reschedule);
      setRescheduleQueryString(false);
    }
  }, [showUnreadQueryString, showRescheduleQueryString]);

  // Map filters to LeadFilterDto structure
  const additionalBody = useMemo(() => {
    const searchBody: any = {};

    // Map status filter - Status is an array in LeadFilterDto
    if (currentStatusFilterIdx !== 9) { // 9 is "All"
      searchBody.Status = [currentStatusFilterIdx];
    }

    // Map website filter
    if (currentWebsiteFilterIdx !== 5) { // 5 is "All"
      searchBody.Website = currentWebsiteFilterIdx;
    }

    // Map document filter - defaults to All (6) if not set
    if (currentDocumentFilterIdx !== 6) { // 6 is "All"
      searchBody.DocumentFilter = currentDocumentFilterIdx;
    } else {
      searchBody.DocumentFilter = 6; // All
    }

    // Map showDropOff - Vehicle.IsDropOff
    if (showDropOff) {
      searchBody.Vehicle = {
        IsDropOff: true
      };
    }

    // Set defaults for awaitingCod and includeCompleted
    // Based on old endpoint defaults: awaitingCod = true, includeCompleted = false
    searchBody.AwaitingCod = true;
    searchBody.IncludeCompleted = false;

    if (showUnread) {
      searchBody.HasUnreadMessages = true;
    }

    return searchBody;
  }, [currentStatusFilterIdx, currentWebsiteFilterIdx, currentDocumentFilterIdx, showDropOff, showUnread]);

  return (
    <>
      <Modal open={exportOpen} setOpen={setExportOpen} width="sm:max-w-3xl">
        <ExportLeads
          onCancel={() => setExportOpen(false)}
          onExport={() => console.log()}
        />
      </Modal>
      <Modal open={importOpen} setOpen={setImportOpen} width="sm:max-w-3xl">
        <ImportLeads
          onCancel={() => setImportOpen(false)}
          onImport={() => console.log()}
        />
      </Modal>

      <LeadStats />

      {/* <div className="mt-4">
        <div>
          <p className="text-sm text-gray-500">
            Last SCC import:{" "}
            {lastSccImport?.created
              ? DateTime.fromISO(lastSccImport?.created).toLocaleString(
                DateTime.DATE_MED_WITH_WEEKDAY
              )
              : "Never"}
          </p>
        </div>
      </div> */}

      <div className="mt-4 flex justify-between">
        <div className="flex gap-x-5">
          <select
            aria-label="Lead status filter"
            onChange={(e) => {
              setCurrentStatusFilter(e.target.value);
              setCurrentStatusFilterIdx(e.target.selectedIndex);
            }}
            value={currentStatusFilter}
            className="sm:text-sm"
          >
            {statusFilters.map((filter) => (
              <option className="sm:text-sm" key={filter}>
                {String(filter)}
              </option>
            ))}
          </select>

          <select
            aria-label="Lead website filter"
            onChange={(e) => {
              setCurrentWebsiteFilter(e.target.value);
              setCurrentWebsiteFilterIdx(e.target.selectedIndex);
            }}
            value={currentWebsiteFilter}
            className="sm:text-sm"
          >
            {websiteFilters.map((filter) => (
              <option className="sm:text-sm" key={filter}>
                {String(filter)}
              </option>
            ))}
          </select>

          <select
            aria-label="Lead document filter"
            onChange={(e) => {
              setCurrentDocumentFilter(e.target.value);
              setCurrentDocumentFilterIdx(e.target.selectedIndex);
            }}
            value={currentDocumentFilter}
            className="sm:text-sm"
          >
            {documentFilters.map((filter) => (
              <option className="sm:text-sm" key={filter}>
                {String(filter)}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            <input
              id="showUnread"
              onChange={(e) => setShowUnread(e.target.checked)}
              value="showUnread"
              checked={showUnread}
              type="checkbox"
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="showUnread">Show Unread</label>
          </div>

          <div className="flex items-center">
            <input
              id="showDropOff"
              onChange={(e) => setShowDropOff(e.target.checked)}
              value="showDropOff"
              checked={showDropOff}
              type="checkbox"
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="showDropOff">Show Dropoff</label>
          </div>
        </div>
        <div className="flex items-center gap-x-2">
          <button
            onClick={() => setExportOpen(true)}
            className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            <DocumentArrowDownIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            Export to CSV
          </button>
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            <DocumentArrowDownIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            Import from CSV
          </button>

          <button
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
            type="button"
            onClick={() => navigate("/leads/create-multiple")}
          >
            Create Lead
          </button>
        </div>
      </div>
      <LeadsTable
        additionalBody={additionalBody}
        showUnread={showUnread}
        unreadMessages={unreadMessages.data?.data}
      />
    </>
  );
}
